// ShareButton.jsx
import React, { useState, useCallback } from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import CopyLinkBox from '../container/CopyLinkBox.js';
import Loading from '../component/Loading.jsx';
import Tooltip from '../component/Tooltip.jsx';

import useShortShareLink, { ShareType } from '../hook/useShortShareLink.js';

import ShareIcon from '../../img/ic-share-24.svg';
import CopyIcon from '../../img/ic-copy-link.svg';

import ResetButtonStyle from '../style/ResetButtonStyle.js';
import media from '../style/media.js';
import { getIsOnMobile } from '../resource/getUserAgent.js';
import copyTextToClipboard from '../resource/copyTextToClipboard.js';
import getCanShare from '../resource/getCanShare.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';

export const ShareButton = ({
  id,
  tooltipPosition,
  shareType,
  marginRight,
  marginLeft,
  marginBottom,
  isOnMobile,
  shareTitleI18nKey,
  shareTitleI18nOption,
  username,
  setClickedShareButton,
  copyLinkBoxPosition,
  pushToastr,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const canShare = getCanShare();
  const handleShare = async shortShareLink => {
    setClickedShareButton({ clicked: true });
    if (canShare) {
      navigator.share({
        title: 'SWAG',
        text: t(shareTitleI18nKey, {
          ...shareTitleI18nOption,
          username,
        }),
        url: shortShareLink,
      });
    } else {
      try {
        await copyTextToClipboard(shortShareLink);
        pushToastr({
          textKey: 'story_copy_link',
          durationSec: 3,
          color: isOnMobile ? 'success' : 'info',
        });
      } catch (_) {
        setIsOpen(true);
      }
    }
  };
  const [isFetchingShortShareLink, onHandleShare] = useShortShareLink({
    targetId: id,
    shareType,
    onShortShareLinkFetched: handleShare,
  });

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <ShareButtonWrapper>
      <StyledShareButton
        onClick={onHandleShare}
        marginRight={marginRight}
        marginLeft={marginLeft}
        marginBottom={marginBottom}
        disabled={isFetchingShortShareLink}
        shareType={shareType}
      >
        {isFetchingShortShareLink ? (
          <Loading />
        ) : canShare ? (
          <img src={ShareIcon} alt="share" width={32} height={32} />
        ) : (
          <Tooltip
            position={tooltipPosition}
            renderHint={() => (
              <TooltipStyle>
                {t('copy_link', {
                  ns: TranslationNamespace.GENERAL,
                })}
              </TooltipStyle>
            )}
            trigger="hover"
            disabled={getIsOnMobile()}
          >
            <img src={CopyIcon} alt="copy" width={32} height={32} />
          </Tooltip>
        )}
      </StyledShareButton>

      {isOpen && (
        <CopyLinkBox
          id={id}
          shareType={shareType}
          handleClose={handleClose}
          position={copyLinkBoxPosition}
        />
      )}
    </ShareButtonWrapper>
  );
};

ShareButton.propTypes = {
  shareType: PropTypes.string,
  username: PropTypes.string,
  id: PropTypes.string,
  tooltipPosition: PropTypes.string,
  marginRight: PropTypes.number,
  marginLeft: PropTypes.number,
  marginBottom: PropTypes.number,
  isOnMobile: PropTypes.bool,
  setClickedShareButton: PropTypes.func,
  pushToastr: PropTypes.func,
  shareTitleI18nKey: PropTypes.string,
  shareTitleI18nOption: PropTypes.object,
  copyLinkBoxPosition: PropTypes.oneOf(['right', 'left', 'center']),
};

ShareButton.defaultProps = {
  shareType: null,
  username: '',
  id: '',
  tooltipPosition: 'top',
  marginRight: 0,
  marginLeft: 0,
  marginBottom: 0,
  isOnMobile: false,
  setClickedShareButton: () => null,
  pushToastr: () => null,
  shareTitleI18nKey: 'title_share_flix_link',
  shareTitleI18nOption: {},
  copyLinkBoxPosition: 'right',
};

const ShareButtonWrapper = styled.div`
  position: relative;
`;

const StyledShareButton = styled.button`
  ${ResetButtonStyle}
  margin-right: ${({ marginRight }) => marginRight}px;
  margin-left: ${({ marginLeft }) => marginLeft}px;
  margin-bottom: ${({ marginBottom }) => marginBottom}px;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  padding: 4px;

  ${({ shareType }) => {
    if (shareType === ShareType.LIVESTREAM) {
      return css`
        background-color: rgba(71, 71, 71, 0.8);
      `;
    }
    return css`
      @media (hover: hover) {
        :hover {
          background-color: rgba(155, 155, 155, 0.2);
        }
      }
      ${media.mobile`
        width: 32px;
        height: 32px;
        padding: 0;
      `}
    `;
  }}
`;

export const TooltipStyle = styled.div`
  height: 30px;
  padding: 8px;
  border-radius: 8px;
  box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.5);
  background-color: rgba(246, 246, 246, 0.8);
  font-size: 14px;
  text-align: center;
  white-space: nowrap;
  color: #191919;
  min-width: 72px;
  height: 30px;
  margin-bottom: 4px;
`;

export default ShareButton;
